// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { WORDLIST } from '../data/wordlist';

/**
 * Nine words from a 390-word list is 77.5 bits (log2(390) = 8.607 bits each).
 *
 * Six words — the obvious default — is only 51.6 bits, and that is not enough here. PBKDF2-SHA256
 * is GPU-friendly: at 600,000 iterations a single RTX-4090-class card manages roughly 14,300
 * guesses per second, so eight cards for a year is about 2^41.7 attempts. Anything under ~45
 * bits of passphrase entropy is dead, and the KDF will not save it.
 */
export const DEFAULT_WORDS = 9;

export function passphraseBits(words: number): number {
  return words * Math.log2(WORDLIST.length);
}

/**
 * Rejection sampling, not modulo.
 *
 * `random % 390` is biased towards the low end of the list — small, but it is free to avoid and
 * this is the only thing standing between an attacker and every value in the mapping.
 */
export function generatePassphrase(words = DEFAULT_WORDS): string {
  const n = WORDLIST.length;
  const limit = Math.floor(65536 / n) * n;
  const out: string[] = [];
  const buf = new Uint16Array(32);
  let i = buf.length;
  while (out.length < words) {
    if (i >= buf.length) {
      crypto.getRandomValues(buf);
      i = 0;
    }
    const v = buf[i++]!;
    if (v >= limit) continue;
    out.push(WORDLIST[v % n]!);
  }
  return out.join('-');
}
